// src/components/AppointmentForm.jsx
import { useState } from 'react';
import SectionTitle from '../common/SectionTitle';
import Button from '../common/Button';

// Replace with your actual clinic image if available
const clinicImage = 'https://btydentalwa.com/wp-content/uploads/Smiling-and-satisfied-patient-in-a-dental-office-after-treatment.jpg'; // shorter, happy patient vibe

const AppointmentForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      alert("Appointment request sent! We'll contact you soon.");
    }, 1500);
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-gradient-to-br from-blue-50 via-indigo-50/40 to-white relative overflow-hidden">
      {/* Lighter, smaller background decoration */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-blue-200 rounded-full blur-3xl" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-indigo-100 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-5 sm:px-6 lg:px-8 relative">
        <SectionTitle
          title="Schedule Your Visit Today"
          subtitle="Experience gentle, advanced dental care. Our team usually responds within 24 hours."
          className="mb-10 md:mb-12 text-center max-w-4xl mx-auto"
        />

        {/* Two-column layout – left: image + content | right: form */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          {/* LEFT: Compact image + clean content */}
          <div className="flex flex-col space-y-6 lg:pr-4 order-2 lg:order-1">
            <div className="relative rounded-2xl overflow-hidden shadow-xl shadow-blue-100/30 border-4 border-white">
              <img
                src={clinicImage}
                alt="Happy patient smiling after comfortable dental treatment at Dr. Sridhar Dental"
                className="w-full h-auto object-cover aspect-[5/3] lg:aspect-[5/3]"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent flex items-end p-5">
                <p className="text-white text-base font-medium drop-shadow">
                  Comfortable Care • Modern Technology • Since 1995
                </p>
              </div>
            </div>

            {/* Reduced vertical space content */}
            <div className="space-y-4 text-center lg:text-left">
              <h3 className="text-xl md:text-2xl font-bold text-gray-800">
                Why Choose Dr. Sridhar Dental
              </h3>

              <ul className="space-y-3 text-gray-700 text-base">
                <li className="flex items-center gap-3">
                  <span className="text-green-500 text-lg font-bold">✓</span>
                  Painless treatments with advanced equipment
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-green-500 text-lg font-bold">✓</span>
                  Expert care in implants, braces & cosmetics
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-green-500 text-lg font-bold">✓</span>
                  Flexible timings in Vijayawada & Hyderabad
                </li>
                <li className="flex items-center gap-3">
                  <span className="text-green-500 text-lg font-bold">✓</span>
                  Thousands of happy, confident smiles
                </li>
              </ul>

              <p className="text-gray-600 text-sm italic pt-1">
                "Your comfort and smile are our top priority."
              </p>
            </div>
          </div>

          {/* RIGHT: Form – aligned height & clean */}
          <div
            className="
              bg-blue-200 backdrop-blur-md 
              rounded-2xl 
              shadow-xl shadow-indigo-100/30 
              border border-gray-100
              p-6 sm:p-8 md:p-9 lg:p-10 
              flex flex-col justify-between
              transition-all duration-300 hover:shadow-indigo-200/20
              order-1 lg:order-2
            "
          >
            <form onSubmit={handleSubmit} className="space-y-5 flex-grow flex flex-col">
              {/* Full Name */}
              <div className="relative">
                <input
                  type="text"
                  id="name"
                  required
                  className="
                    peer w-full px-5 py-4 pb-2 border border-gray-300 rounded-xl
                    focus:border-blue-500 focus:ring-1 focus:ring-blue-200 focus:outline-none
                    transition-all duration-200 bg-white
                    placeholder-transparent text-gray-900
                  "
                  placeholder="Full Name"
                />
                <label
                  htmlFor="name"
                  className="
                    absolute left-5 top-3.5 text-gray-500 text-sm transition-all duration-200
                    peer-placeholder-shown:text-base peer-placeholder-shown:top-4.5 peer-placeholder-shown:text-gray-400
                    peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-blue-600 font-medium
                    pointer-events-none
                  "
                >
                  Full Name *
                </label>
              </div>

              {/* Email */}
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  required
                  className="peer w-full px-5 py-4 pb-2 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-200 focus:outline-none transition-all duration-200 bg-white placeholder-transparent text-gray-900"
                  placeholder="Email Address"
                />
                <label
                  htmlFor="email"
                  className="absolute left-5 top-3.5 text-gray-500 text-sm transition-all duration-200 peer-placeholder-shown:text-base peer-placeholder-shown:top-4.5 peer-placeholder-shown:text-gray-400 peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-blue-600 font-medium pointer-events-none"
                >
                  Email Address *
                </label>
              </div>

              {/* Phone */}
              <div className="relative">
                <input
                  type="tel"
                  id="phone"
                  required
                  className="peer w-full px-5 py-4 pb-2 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-200 focus:outline-none transition-all duration-200 bg-white placeholder-transparent text-gray-900"
                  placeholder="Phone Number"
                />
                <label
                  htmlFor="phone"
                  className="absolute left-5 top-3.5 text-gray-500 text-sm transition-all duration-200 peer-placeholder-shown:text-base peer-placeholder-shown:top-4.5 peer-placeholder-shown:text-gray-400 peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-blue-600 font-medium pointer-events-none"
                >
                  Phone Number *
                </label>
              </div>

              {/* Message */}
              <div className="relative flex-grow">
                <textarea
                  id="message"
                  rows={3}
                  className="peer w-full px-5 py-4 pb-2 border border-gray-300 rounded-xl focus:border-blue-500 focus:ring-1 focus:ring-blue-200 focus:outline-none transition-all duration-200 bg-white resize-none placeholder-transparent text-gray-900 h-full min-h-[100px]"
                  placeholder="Preferred date/time or message (optional)"
                />
                <label
                  htmlFor="message"
                  className="absolute left-5 top-3.5 text-gray-500 text-sm transition-all duration-200 peer-placeholder-shown:text-base peer-placeholder-shown:top-4.5 peer-placeholder-shown:text-gray-400 peer-focus:top-1.5 peer-focus:text-xs peer-focus:text-blue-600 font-medium pointer-events-none"
                >
                  Preferred Date / Message (optional)
                </label>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className={`
                  w-full py-3.5 text-base md:text-lg font-semibold rounded-xl mt-2
                  bg-gradient-to-r from-blue-600 to-indigo-600
                  hover:from-blue-700 hover:to-indigo-700
                  text-white shadow-md shadow-blue-200/30
                  transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5
                  disabled:opacity-60 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2.5
                `}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Sending...
                  </>
                ) : (
                  "Book Appointment"
                )}
              </Button>
            </form>

            <p className="text-center text-xs md:text-sm text-gray-500 mt-4">
              Your details are secure • We respect your privacy
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AppointmentForm;